// ✅ src/pages/VehicleV2/BatteryCardMaintainPrint.jsx
// Prints like your screenshot "Battery Card Maintenance Report" (single battery card)
//
// Expected navigation:
//   navigate("/vehicle/BatteryCardMaintainPrint", { state: { batteryId } })
//
// Backend expected (from earlier backend we built):
//   GET https://nissi-india.com/api/batteryCardMaintenanceReport/print/:batteryId
//
// NOTE:
// Your DB/proc may return different column names for some fields (issued/current branch,
// battery make, supplier, src serial, issued date, status). This component maps with
// safe fallbacks, so it won’t break.

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/batteryCardMaintenanceReport";

const toStr = (v) => String(v ?? "").trim();

const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("en-GB"); // dd/MM/yyyy
  return String(v);
};

const fmtDateTime = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (!Number.isNaN(d.getTime())) {
    // dd/MM/yyyy hh:mm am/pm
    const ddmmyy = d.toLocaleDateString("en-GB");
    const time = d
      .toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
      .toLowerCase();
    return `${ddmmyy} ${time}`;
  }
  return String(v);
};

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function pick(row, keys, def = "") {
  for (const k of keys) {
    const val = row?.[k];
    if (val !== undefined && val !== null && String(val).trim() !== "") return val;
  }
  return def;
}

export default function BatteryCardMaintainPrint() {
  const navigate = useNavigate();
  const location = useLocation();

  const batteryIdFromState = location?.state?.batteryId;
  const batteryIdFromQuery = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.get("batteryId");
  }, [location.search]);

  const batteryId = batteryIdFromState || batteryIdFromQuery;

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [row, setRow] = useState(null);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        if (!batteryId) throw new Error("batteryId not provided");

        const res = await axios.get(`${API_BASE}/print/${batteryId}`);
        const r = res?.data?.row;

        if (!res?.data?.success || !r) throw new Error(res?.data?.message || "No data");

        if (alive) setRow(r);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.message || e.message || "Failed to load print");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [batteryId]);

  // ---- Normalize fields to match screenshot labels ----
  const view = useMemo(() => {
    const r = row || {};


    return {
      vehicleName: toStr(pick(r, ["VehicleName", "fld_vehicle_name", "vehicleName"])),
      vehicleRegNo: toStr(pick(r, ["VehicleRegNo", "vehicleRegNo"])),
      vehicleCode: toStr(pick(r, ["VehicleCode", "vehicleCode"])),

      issuedBranch: toStr(pick(r, ["IssuedBranch", "fld_issuedbranch", "fld_IssuedBranch", "fld_BranchName"])),
      currentBranch: toStr(pick(r, ["CurrentBranch", "fld_currentbranch", "fld_CurrentBranch", "fld_BranchName"])),

      batteryMake: toStr(pick(r, ["fld_batterymake", "BatteryMake"])),
      batteryType: toStr(pick(r, ["fld_batterytype", "BatteryType"])),
      batteryCode: toStr(pick(r, ["fld_batterycode", "BatteryCode"])),
      batterySerialNo: toStr(pick(r, ["fld_battery_serialno", "fld_battery_serialno", "BatterySerialNo"])),

      srcSerialNo: toStr(pick(r, ["fld_src_serialno", "SrcSerialNo"])),
      supplierName: toStr(pick(r, ["fld_suppliername", "SupplierName", "fld_supplier"])),
      fittedDate: fmtDate(pick(r, ["fld_Fitteddate", "FittedDate"])),
      currentRemoveDate: fmtDate(pick(r, ["CurrentRemoveDate", "removeDate", "fld_removedate"])),
      issuedDate: fmtDate(pick(r, ["fld_dateofreceipt", "IssuedDate", "fld_issueddate"])),

      lifeDays: toStr(pick(r, ["fld_currentlife", "LifeDays", "fld_life"])),
      batteryCost: fmtMoney(pick(r, ["fld_purchaseamt", "BatteryCost", "purchaseAmt"])),
      perDayCosting: fmtMoney(pick(r, ["PerDayCosting", "perDayCost", "perDayCosting"])),

      batteryStatus: toStr(pick(r, ["fld_batterystatus", "BatteryStatus"], "Process")),

      // top right
      dateTime: fmtDateTime(new Date()),
    };
  }, [row]);

  return (
    <div className="w-full bg-white">
      <style>{`
        @page { size: A4 portrait; margin: 12mm; }
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      {/* top actions */}
      <div className="no-print flex items-center justify-between p-3 border-b">
        <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
          ← Back
        </button>
        <button
          onClick={() => window.print()}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded"
        >
          🖨️ Print
        </button>
      </div>

      {/* page */}
      <div className="max-w-[820px] mx-auto p-4">
            <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">Nissi</div>
              <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
              <div className="text-xs">
                3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
              </div>
              <div className="text-xs">
                Phone No: 9003744644&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;infracnsoft-implementation@nissiinfotech.in
              </div>
            </div>
          </div>
        </div>
        {/* Outer border like screenshot */}
        <div className="border border-black">
          {/* Title */}
          <div className="text-center font-bold underline py-2">
            Battery Card Maintenance Report
          </div>

          {/* Content block */}
          <div className="px-8 py-4 text-[13px]">
            {/* Row: Vehicle Name + DateTime */}
            <div className="flex justify-between">
              <div className="w-[60%]">
                <Line label="Vehicle Name" value={view.vehicleName} />
              </div>
              <div className="w-[40%]">
                <Line label="Date and Time" value={view.dateTime} />
              </div>
            </div>

            <Line label="Vehicle Reg Number" value={view.vehicleRegNo} />
            <Line label="Vehicle Code" value={view.vehicleCode} />
            <Line label="Issued Branch" value={view.issuedBranch} />
            <Line label="Current Branch" value={view.currentBranch} />
            <Line label="Batery Make" value={view.batteryMake} />
            <Line label="Battery Type" value={view.batteryType} />
            <Line label="Battery Code" value={view.batteryCode} />
            <Line label="Battery serial Number" value={view.batterySerialNo} />
            <Line label="Src serial Number" value={view.srcSerialNo} />
            <Line label="Supplier Name" value={view.supplierName} />
            <Line label="Fitted Date" value={view.fittedDate} />
            <Line label="current /Removedate" value={view.currentRemoveDate} />
            <Line label="Issued date" value={view.issuedDate} />
            <Line label="Life (days)" value={view.lifeDays} />
            <Line label="Battery Cost" value={view.batteryCost} />
            <Line label="Per Day Costing" value={view.perDayCosting} />
            <Line label="Battery Status" value={view.batteryStatus} />

            {/* Battery Testing Report section */}
            <div className="mt-6 font-bold underline">Battery Testing Report</div>

            <div className="mt-3 border border-black">
              <table className="w-full border-collapse text-[12px]">
                <thead>
                  <tr>
                    <th className="border border-black px-2 py-1 text-left" rowSpan={2}>
                      Description
                    </th>
                    <th className="border border-black px-2 py-1 text-center" colSpan={1}>
                      Voltmeter test
                    </th>
                    <th className="border border-black px-2 py-1 text-center" colSpan={6}>
                      Hydrometer test/Specific gravity test
                    </th>
                  </tr>
                  <tr>
                    <th className="border border-black px-2 py-1 text-center">Voltage</th>
                    <th className="border border-black px-2 py-1 text-center">Cell 1</th>
                    <th className="border border-black px-2 py-1 text-center">Cell 2</th>
                    <th className="border border-black px-2 py-1 text-center">Cell 3</th>
                    <th className="border border-black px-2 py-1 text-center">Cell 4</th>
                    <th className="border border-black px-2 py-1 text-center">Cell 5</th>
                    <th className="border border-black px-2 py-1 text-center">Cell 6</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-black px-2 py-6">&nbsp;</td>
                    <td className="border border-black px-2 py-6">&nbsp;</td>
                    <td className="border border-black px-2 py-6">&nbsp;</td>
                    <td className="border border-black px-2 py-6">&nbsp;</td>
                    <td className="border border-black px-2 py-6">&nbsp;</td>
                    <td className="border border-black px-2 py-6">&nbsp;</td>
                    <td className="border border-black px-2 py-6">&nbsp;</td>
                    <td className="border border-black px-2 py-6">&nbsp;</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* footer */}
          <div className="border-t border-black px-3 py-3 text-[12px]">
            Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
          </div>
        </div>

        {/* Loading / error shown only on screen */}
        {loading && (
          <div className="no-print mt-3 p-3 border rounded bg-gray-50 text-sm">
            Loading...
          </div>
        )}
        {err && (
          <div className="no-print mt-3 p-3 border rounded bg-red-50 text-sm text-red-700">
            {err}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- small line component (label : value) ---------- */
function Line({ label, value }) {
  return (
    <div className="flex gap-3 py-1">
      <div className="w-[170px] font-semibold">{label}</div>
      <div className="w-[10px] font-semibold">:</div>
      <div className="flex-1">{value || ""}</div>
    </div>
  );
}
